// Company tags: a company's own labels ("BI & dashboards", "Tableau"),
// matched against the tenders by the backend on every load (GET
// /company-tags; nothing is stored). This module owns everything
// non-visual — joining the matches onto the list, badges, the "Your tags"
// filter, evidence texts, the editor's validation and preview counts; the
// JSX (CompanyTags.tsx, CompanyTagsDialog.tsx) only renders.

export interface CompanyTag {
    tagId: string;
    name: string;
    explanation: string | null;
    keywords: string[];
    minSimilarity: number;
    updatedBy: string | null;
    updatedAt: string | null;
}

export interface CompanyTagMatch {
    tagId: string;
    tenderId: string;
    similarity: number | null;  // a meaning match
    keyword: string | null;     // a keyword match: the phrase found…
    source: string | null;      // …in this file, or 'title'
}

export interface CompanyTagsData {
    tags: CompanyTag[];
    matches: CompanyTagMatch[];
    keywordMatchesUnavailable: boolean;
    unavailableKeywords: string[];  // phrases whose search timed out; they match nothing
}

export interface TenderTagHit {
    tag: CompanyTag;
    match: CompanyTagMatch;
}

export interface TagDraft {
    name: string;
    explanation: string;
    keywords: string[];
    minSimilarity: number;
}

export interface TagPreview {
    candidates: { tenderId: string; similarity: number }[];
    keywordHits: { tenderId: string; keyword: string; source: string; snippet: string | null }[];
    keywordMatchesUnavailable: boolean;
    unavailableKeywords: string[];
}

export const EMPTY_COMPANY_TAGS: CompanyTagsData = {
    tags: [], matches: [], keywordMatchesUnavailable: false, unavailableKeywords: [],
};
export const DEFAULT_MIN_SIMILARITY = 0.4;
export const SLIDER_RANGE: [number, number] = [0.25, 0.7];
export const MAX_ROW_BADGES = 2;
// Mirrors company_tags.validate_tag in the tender Lambda.
export const LIMITS = { name: 40, explanation: 500, keywords: 10, keywordMin: 2, keywordMax: 60 };

export function parseCompanyTags(data: any): CompanyTagsData {
    return {
        tags: Array.isArray(data?.tags) ? data.tags : [],
        matches: Array.isArray(data?.matches) ? data.matches : [],
        keywordMatchesUnavailable: Boolean(data?.keywordMatchesUnavailable),
        unavailableKeywords: Array.isArray(data?.unavailableKeywords) ? data.unavailableKeywords : [],
    };
}

// tender id → its tag hits, ordered by tag name. Matches of a tag that is
// no longer in the list (deleted meanwhile) are dropped.
export function hitsByTender(data: CompanyTagsData): Map<string, TenderTagHit[]> {
    const tags = new Map(data.tags.map(t => [t.tagId, t]));
    const out = new Map<string, TenderTagHit[]>();
    for (const match of data.matches) {
        const tag = tags.get(match.tagId);
        if (!tag) continue;
        const list = out.get(match.tenderId) ?? [];
        list.push({ tag, match });
        out.set(match.tenderId, list);
    }
    out.forEach(list => list.sort((a, b) => a.tag.name.localeCompare(b.tag.name)));
    return out;
}

// Row badges: the first MAX_ROW_BADGES tags, the rest as "+N".
export function rowTagBadges(hits: TenderTagHit[] | undefined): { shown: TenderTagHit[]; more: number } {
    const all = hits ?? [];
    return { shown: all.slice(0, MAX_ROW_BADGES), more: Math.max(0, all.length - MAX_ROW_BADGES) };
}

// Why a tender carries a tag, for tooltips and the detail panel.
export function tagEvidence(match: CompanyTagMatch): string {
    const parts: string[] = [];
    if (match.keyword) {
        parts.push(`found “${match.keyword}” in ${match.source === 'title' ? 'the title' : match.source}`);
    }
    if (match.similarity != null) parts.push(`${Math.round(match.similarity * 100)} % similar`);
    return parts.join(' · ');
}

// The selection minus tags that no longer exist: an unknown id in the
// filter would hide every tender.
export function activeTagSelection(selected: string[], tags: CompanyTag[]): string[] {
    const known = new Set(tags.map(t => t.tagId));
    return selected.filter(id => known.has(id));
}

// "Your tags": a tender passes with ANY selected tag; nothing selected = all
// pass. Uploaded RFPs always pass: they carry no tags, so a selection would
// silently empty the RFP list (the SIMAP facets exempt them the same way).
export function passesCompanyTagFilter(tenderId: string, selected: string[],
                                       byTender: Map<string, TenderTagHit[]>, projectType?: string): boolean {
    if (selected.length === 0 || projectType === 'rfp') return true;
    return (byTender.get(tenderId) ?? []).some(h => selected.includes(h.tag.tagId));
}

// Filter counts: tenders per tag among the given ones (the list before tag filtering).
export function companyTagCounts(tenderIds: string[], byTender: Map<string, TenderTagHit[]>): Record<string, number> {
    const counts: Record<string, number> = {};
    for (const id of tenderIds) {
        for (const hit of byTender.get(id) ?? []) counts[hit.tag.tagId] = (counts[hit.tag.tagId] ?? 0) + 1;
    }
    return counts;
}

// Manager counts: all and open tenders per tag, only tenders the list holds.
export function tagMatchCounts(byTender: Map<string, TenderTagHit[]>,
                               known: (tenderId: string) => { open: boolean } | undefined,
): Record<string, { total: number; open: number }> {
    const counts: Record<string, { total: number; open: number }> = {};
    byTender.forEach((hits, tenderId) => {
        const info = known(tenderId);
        if (!info) return;
        hits.forEach(h => {
            const c = counts[h.tag.tagId] ?? (counts[h.tag.tagId] = { total: 0, open: 0 });
            c.total += 1;
            if (info.open) c.open += 1;
        });
    });
    return counts;
}

// The editor's first problem with a draft, or null — mirrors the API.
export function validateTagDraft(draft: TagDraft): string | null {
    const name = draft.name.trim();
    if (!name) return 'Give the tag a name.';
    if (name.length > LIMITS.name) return `The name can have at most ${LIMITS.name} characters.`;
    const explanation = draft.explanation.trim();
    if (explanation.length > LIMITS.explanation) {
        return `The explanation can have at most ${LIMITS.explanation} characters.`;
    }
    const keywords = draft.keywords.map(k => k.trim()).filter(Boolean);
    if (keywords.length > LIMITS.keywords) return `At most ${LIMITS.keywords} keywords.`;
    const bad = keywords.find(k => k.length < LIMITS.keywordMin || k.length > LIMITS.keywordMax
        || !/[0-9a-zà-ÿ]/i.test(k));
    if (bad) {
        return `“${bad}” is not a usable keyword (${LIMITS.keywordMin}–${LIMITS.keywordMax} characters, with a letter or digit).`;
    }
    if (!explanation && keywords.length === 0) return 'Add an explanation, keywords, or both.';
    return null;
}

// "37 tenders match (12 open)" in the editor: candidates at or above the
// cutoff plus keyword hits, each tender once, only tenders the list holds.
export function previewMatches(preview: TagPreview, cutoff: number,
                               known: (tenderId: string) => { open: boolean } | undefined,
): { total: number; open: number } {
    const ids = new Set<string>();
    preview.candidates.forEach(c => { if (c.similarity >= cutoff) ids.add(c.tenderId); });
    preview.keywordHits.forEach(h => ids.add(h.tenderId));
    let total = 0;
    let open = 0;
    ids.forEach(id => {
        const info = known(id);
        if (!info) return;
        total += 1;
        if (info.open) open += 1;
    });
    return { total, open };
}

export function draftFromTag(tag: CompanyTag | null): TagDraft {
    return tag
        ? { name: tag.name, explanation: tag.explanation ?? '', keywords: [...tag.keywords], minSimilarity: tag.minSimilarity }
        : { name: '', explanation: '', keywords: [], minSimilarity: DEFAULT_MIN_SIMILARITY };
}

// The request body for POST/PUT /company-tags and the preview.
export function draftPayload(draft: TagDraft) {
    return {
        name: draft.name.trim(),
        explanation: draft.explanation.trim() || null,
        keywords: draft.keywords.map(k => k.trim()).filter(Boolean),
        minSimilarity: Math.round(draft.minSimilarity * 100) / 100,
    };
}

// Why some keywords match nothing right now: their search took too long (a
// phrase whose words are common everywhere, e.g. "C++" = the letter "c").
// Named, so the user can replace them; null when every keyword was searched.
export function keywordProblem(unavailable: string[], keywordMatchesUnavailable: boolean): string | null {
    if (unavailable.length === 0) {
        return keywordMatchesUnavailable ? 'Keyword matching is unavailable right now.' : null;
    }
    const quoted = unavailable.map(k => `“${k}”`);
    const list = quoted.length === 1 ? quoted[0] : `${quoted.slice(0, -1).join(', ')} and ${quoted[quoted.length - 1]}`;
    return unavailable.length === 1
        ? `Searching for ${list} took too long, so it matches nothing. Use a longer, more specific phrase.`
        : `Searching for ${list} took too long, so they match nothing. Use longer, more specific phrases.`;
}
